import React from "react";
import {
    Grid,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TablePagination,
    TableRow,
    TextField,
    Typography
} from "@material-ui/core";
import { makeStyles, Theme, createStyles } from "@material-ui/core/styles";
import { Alert } from "@material-ui/lab";
import { useQueryClient } from "react-query";
import { useTranslation } from "react-i18next";
import { apiDirectCall } from "customHooks/useApi";
import Loading from "customHooks/useCompleoForm/helpers/Loading";
import useList from "../..";
import useGetMetadata from "customHooks/useGetMetadata";
import { useAuthState } from "_ReactContext/AuthContext";
import useShowMessage from "customHooks/useShowMessage/useShowMessage";
import { getWhatsappApiResponseData } from "functions/whatsappApiResponse";
import { getApiErrorMessage } from "Pages/Applicant/ApplicantView/ApplicantWhatsapp/helpers";
import { IListBulk } from "./helperBulkActions";

interface IProps {
    formikRef: React.MutableRefObject<any>;
    listBulk: IListBulk[];
    setStepBulk: (value: React.SetStateAction<number>) => void;
    setLoadingButtons: React.Dispatch<React.SetStateAction<boolean>>;
}

interface PreviewData {
    total: number;
    eligibleCount: number;
    blockedCount: number;
    eligibleIds: string[];
    items: PreviewItem[];
    reasons: Record<string, number>;
}

interface PreviewItem {
    id: string;
    applicantId: string;
    name: string;
    email: string;
    mobileNumber: string;
    eligible: boolean;
    status: "ELIGIBLE" | "BLOCKED";
    reasonCode: string;
    reason: string;
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {
            display: "flex",
            flexDirection: "column",
            gap: theme.spacing(2)
        },
        stat: {
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 6,
            padding: theme.spacing(1.5),
            minHeight: 76
        },
        statValue: {
            fontWeight: 700
        },
        previewBox: {
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 6,
            padding: theme.spacing(2)
        },
        recipientsTableWrapper: {
            marginTop: theme.spacing(2),
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 6,
            overflowX: "auto"
        },
        recipientsTable: {
            minWidth: 820
        }
    })
);

const getTranslationOrFallback = (t: any, key: string, fallback: string) => {
    const translated = t(key);
    return translated === key ? fallback : translated;
};

const getPreviewReasonLabel = (t: any, item: PreviewItem) => {
    if (item.eligible) {
        return "-";
    }

    return getTranslationOrFallback(
        t,
        `reason_${item.reasonCode}`,
        item.reason || item.reasonCode || "-"
    );
};

const BulkActionsStep3WhatsappManualOptIn = (props: IProps) => {
    const moduleName = "ELASTICLISTSETTINGSBULKWHATSAPPMANUALOPTIN";
    const { formikRef, listBulk, setStepBulk, setLoadingButtons } = props;
    const classes = useStyles();
    const queryClient = useQueryClient();
    const [listData] = useList();
    const showMessage = useShowMessage();
    const { company } = useAuthState();
    const [metadata] = useGetMetadata(moduleName);
    const [t, , readyTranslation] = useTranslation(moduleName, {
        useSuspense: false
    });
    const [bulkName, setBulkName] = React.useState("");
    const [evidenceText, setEvidenceText] = React.useState("");
    const [preview, setPreview] = React.useState<PreviewData | null>(null);
    const [previewLoading, setPreviewLoading] = React.useState(false);
    const [previewError, setPreviewError] = React.useState("");
    const [recipientsPage, setRecipientsPage] = React.useState(0);

    const selectedIds = React.useMemo(
        () =>
            listBulk
                .filter((item) => item.checked)
                .map((item) => item.value)
                .filter(Boolean),
        [listBulk]
    );
    const selectedIdsKey = selectedIds.join("|");
    const recipientsRowsPerPage = 10;
    const previewItems = preview?.items || [];
    const pagedPreviewItems = previewItems.slice(
        recipientsPage * recipientsRowsPerPage,
        recipientsPage * recipientsRowsPerPage + recipientsRowsPerPage
    );

    React.useEffect(() => {
        setRecipientsPage(0);
    }, [preview]);

    React.useEffect(() => {
        let cancelled = false;

        const loadPreview = async () => {
            setPreview(null);
            setPreviewError("");

            if (company.whatsappEnabled !== true || selectedIds.length === 0) {
                setPreviewLoading(false);
                return;
            }

            try {
                setPreviewLoading(true);
                const result = await apiDirectCall(
                    "/whatsapp/bulkoptinpreview",
                    "post",
                    {
                        companyId: company.companyId,
                        ids: selectedIds
                    }
                );
                if (!cancelled) {
                    setPreview(
                        getWhatsappApiResponseData(result) as PreviewData
                    );
                }
            } catch (error) {
                if (!cancelled) {
                    setPreviewError(
                        getApiErrorMessage(error, t("previewError"))
                    );
                }
            } finally {
                if (!cancelled) {
                    setPreviewLoading(false);
                }
            }
        };

        loadPreview();

        return () => {
            cancelled = true;
        };
    }, [company.companyId, company.whatsappEnabled, selectedIdsKey, t]);

    const handleSubmit = React.useCallback(async () => {
        if (!evidenceText.trim()) {
            showMessage(t("evidenceRequired"), "error");
            setLoadingButtons(false);
            return;
        }

        if (!preview || preview.eligibleCount < 1) {
            showMessage(t("noRecipients"), "error");
            setLoadingButtons(false);
            return;
        }

        try {
            setLoadingButtons(true);
            await apiDirectCall("/bulkactions/baadd", "post", {
                companyId: company.companyId,
                ids: preview.eligibleIds,
                bulkSelectionId: listBulk[0]?.bulkSelectionId,
                bulkProtocolVersion: 1,
                name: bulkName,
                evidenceText,
                type: "applicant_whatsappManualOptIn"
            });
            queryClient.invalidateQueries(listData.tempInfo.useApiKey);
            showMessage(t("created"), "success");
            setStepBulk(99);
        } catch (error) {
            showMessage(
                getApiErrorMessage(error, t("createError")),
                "error"
            );
        } finally {
            setLoadingButtons(false);
        }
    }, [
        bulkName,
        company.companyId,
        evidenceText,
        listData.tempInfo.useApiKey,
        preview,
        queryClient,
        setLoadingButtons,
        setStepBulk,
        showMessage,
        t
    ]);

    React.useEffect(() => {
        formikRef.current = {
            submitForm: handleSubmit
        };
    }, [formikRef, handleSubmit]);

    const metadataReady = readyTranslation && metadata.status === "success";

    if (!metadataReady) {
        return <Loading />;
    }

    return (
        <div className={classes.root}>
            {company.whatsappEnabled !== true && (
                <Alert severity="warning">{t("noConnection")}</Alert>
            )}

            <TextField
                fullWidth
                label={t("operationName")}
                helperText={t("operationNameHelper")}
                value={bulkName}
                onChange={(event) => setBulkName(event.target.value)}
            />

            <TextField
                fullWidth
                required
                multiline
                rows={2}
                label={t("evidenceText")}
                helperText={t("evidenceTextHelper")}
                value={evidenceText}
                onChange={(event) => setEvidenceText(event.target.value)}
            />

            <Alert severity="info">{t("eligibleOnlyNotice")}</Alert>

            <div className={classes.previewBox}>
                {previewLoading ? (
                    <Loading />
                ) : previewError ? (
                    <Alert severity="error">{previewError}</Alert>
                ) : preview ? (
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={4}>
                            <div className={classes.stat}>
                                <Typography variant="caption" color="textSecondary">
                                    {t("selected")}
                                </Typography>
                                <Typography
                                    variant="h5"
                                    className={classes.statValue}
                                >
                                    {preview.total}
                                </Typography>
                            </div>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <div className={classes.stat}>
                                <Typography variant="caption" color="textSecondary">
                                    {t("eligible")}
                                </Typography>
                                <Typography
                                    variant="h5"
                                    className={classes.statValue}
                                >
                                    {preview.eligibleCount}
                                </Typography>
                            </div>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <div className={classes.stat}>
                                <Typography variant="caption" color="textSecondary">
                                    {t("blocked")}
                                </Typography>
                                <Typography
                                    variant="h5"
                                    className={classes.statValue}
                                >
                                    {preview.blockedCount}
                                </Typography>
                            </div>
                        </Grid>
                        <Grid item xs={12}>
                            {Object.keys(preview.reasons || {}).length > 0 && (
                                <Typography variant="body2" color="textSecondary">
                                    {Object.entries(preview.reasons)
                                        .map(
                                            ([reason, total]) =>
                                                `${total} ${
                                                    t(`reason_${reason}`)
                                                }`
                                        )
                                        .join(", ")}
                                </Typography>
                            )}
                            {previewItems.length > 0 && (
                                <div className={classes.recipientsTableWrapper}>
                                    <Table
                                        size="small"
                                        className={classes.recipientsTable}
                                    >
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>
                                                    {t("candidateName")}
                                                </TableCell>
                                                <TableCell>
                                                    {t("candidateEmail")}
                                                </TableCell>
                                                <TableCell>
                                                    {t("candidateMobile")}
                                                </TableCell>
                                                <TableCell>
                                                    {t("candidateStatus")}
                                                </TableCell>
                                                <TableCell>
                                                    {t("blockReason")}
                                                </TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {pagedPreviewItems.map((item) => (
                                                <TableRow
                                                    key={
                                                        item.id ||
                                                        item.applicantId
                                                    }
                                                >
                                                    <TableCell>
                                                        {item.name ||
                                                            item.applicantId ||
                                                            "-"}
                                                    </TableCell>
                                                    <TableCell>
                                                        {item.email || "-"}
                                                    </TableCell>
                                                    <TableCell>
                                                        {item.mobileNumber ||
                                                            "-"}
                                                    </TableCell>
                                                    <TableCell>
                                                        {item.eligible
                                                            ? t(
                                                                  "statusEligible"
                                                              )
                                                            : t(
                                                                  "statusBlocked"
                                                              )}
                                                    </TableCell>
                                                    <TableCell>
                                                        {getPreviewReasonLabel(
                                                            t,
                                                            item
                                                        )}
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                    {previewItems.length > recipientsRowsPerPage && (
                                        <TablePagination
                                            component="div"
                                            count={previewItems.length}
                                            page={recipientsPage}
                                            rowsPerPage={recipientsRowsPerPage}
                                            rowsPerPageOptions={[]}
                                            onChangePage={(_, page) =>
                                                setRecipientsPage(page)
                                            }
                                            labelRowsPerPage=""
                                            labelDisplayedRows={({ from, to, count }) =>
                                                t("paginationDisplayedRows", {
                                                    from,
                                                    to,
                                                    count
                                                })
                                            }
                                            backIconButtonText={t(
                                                "previousPage"
                                            )}
                                            nextIconButtonText={t("nextPage")}
                                        />
                                    )}
                                </div>
                            )}
                        </Grid>
                    </Grid>
                ) : (
                    <Typography variant="body2" color="textSecondary">
                        {t("previewPending")}
                    </Typography>
                )}
            </div>
        </div>
    );
};

export default BulkActionsStep3WhatsappManualOptIn;
